/**
 * Gate 2 — API connection collected when selected metrics need rest_api.
 * Reuses Gate 1 auth types. Endpoint path/method come from Gate 1 when
 * declared; they can be edited here for this run.
 */
import {
  AUTH_TYPE_OPTIONS,
  EMPTY_CREDENTIALS,
  authTypeNeedsCredentials,
  normalizeAuthType,
} from "../../utils/apiSurfaceForm";

export { AUTH_TYPE_OPTIONS, EMPTY_CREDENTIALS, authTypeNeedsCredentials };

export const EMPTY_API_CONNECTION_FORM = {
  baseUrl: "",
  authType: "",
  endpoint: "",
  method: "POST",
};

export function apiFormFromGate1Config(config = {}) {
  const rawAuth = String(config.authType || config.auth_type || "").trim();
  const rawMethod = String(config.method || "POST").trim().toUpperCase();
  return {
    baseUrl: String(config.baseUrl || config.base_url || "").trim(),
    authType: AUTH_TYPE_OPTIONS.includes(rawAuth) ? rawAuth : "",
    endpoint: String(config.endpoint || config.path || "").trim(),
    method: rawMethod === "GET" || rawMethod === "POST" ? rawMethod : "POST",
  };
}

/**
 * Copy for the execution prompt when the readiness bearer token cannot be used.
 * Callers must not put the token in the message. The link is the readiness API surface.
 */
export function readinessBearerPrompt(code) {
  const rejected = code === "API_BEARER_REJECTED";
  return {
    title: rejected ? "Bearer token rejected" : "Bearer token required",
    message: rejected
      ? "The bearer token from System Readiness was rejected. Update it there and try again."
      : "The bearer token from System Readiness is missing. Update it there before running metrics.",
  };
}

export function apiFormComplete(form, credentials = {}, options = {}) {
  if (!String(form?.baseUrl || "").trim()) return false;
  if (!String(form?.endpoint || "").trim()) return false;
  if (!authTypeNeedsCredentials(form.authType)) return true;
  const auth = normalizeAuthType(form.authType);
  if (auth.includes("bearer")) return Boolean(options.bearerTokenSaved);
  if (auth.includes("basic")) {
    return Boolean(String(credentials.basicEmail || "").trim() && String(credentials.basicApiToken || "").trim());
  }
  return Boolean(String(credentials.apiKeyValue || "").trim());
}

export function apiCredentialField(authType) {
  if (!authTypeNeedsCredentials(authType)) return null;
  const auth = normalizeAuthType(authType);
  if (auth.includes("bearer")) {
    return {
      key: "bearerToken",
      label: "Bearer token",
      placeholder: "Not stored — used for this run only",
    };
  }
  if (auth.includes("basic")) {
    return {
      key: "basicApiToken",
      label: "API token",
      placeholder: "Not stored — used for this run only",
    };
  }
  return {
    key: "apiKeyValue",
    label: "API key",
    placeholder: "Not stored — used for this run only",
  };
}

export function apiConnectionSummaryRows(form) {
  return [
    { key: "baseUrl", label: "Base URL", value: form?.baseUrl },
    { key: "endpoint", label: "Endpoint", value: form?.endpoint },
    { key: "method", label: "Method", value: form?.method },
    { key: "authType", label: "Auth type", value: form?.authType },
  ].filter((row) => String(row.value ?? "").trim());
}

/**
 * Request payload for POST /gate2/execution/collect-evidence (api_config).
 * Secrets stay in a separate credentials object.
 */
export function buildApiPayload(form, credentials = {}) {
  const endpoint = String(form.endpoint || "").trim();
  return {
    api_config: {
      baseUrl: String(form.baseUrl || "").trim(),
      authType: String(form.authType || "").trim(),
      endpoint: endpoint.startsWith("/") || !endpoint ? endpoint : `/${endpoint}`,
      method: String(form.method || "POST").trim().toUpperCase() || "POST",
      ...(form.questionField ? { questionField: form.questionField } : {}),
      answerPath: form.answerPath || "answer",
    },
    credentials: {
      bearerToken: credentials.bearerToken || "",
      basicEmail: credentials.basicEmail || "",
      basicApiToken: credentials.basicApiToken || "",
      apiKeyHeader: credentials.apiKeyHeader || "",
      apiKeyValue: credentials.apiKeyValue || "",
    },
  };
}

function utf8ToBase64(str) {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
  );
}

/**
 * Request payload for POST /readiness/system-check from the Gate 2 API card.
 */
export function buildApiProbeConfig(form, credentials = {}) {
  const baseUrl = String(form?.baseUrl || "").trim().replace(/\/+$/, "");
  if (!baseUrl) return { error: "Enter a base URL before testing." };
  if (!/^https?:\/\//i.test(baseUrl)) {
    return { error: "Base URL must start with https:// or http://." };
  }
  const rawPath = String(form?.endpoint || "").trim();
  if (!rawPath) return { error: "Enter an endpoint path before testing." };
  const path = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
  const method = String(form?.method || "POST").trim().toUpperCase() === "GET" ? "GET" : "POST";
  const auth = normalizeAuthType(form?.authType);

  let authConfigs = { "auth-none": { type: "none" } };
  let defaultAuthRef = "auth-none";

  if (auth.includes("bearer")) {
    const token = String(credentials.bearerToken || "").trim();
    if (!token) return { error: "Enter the bearer token before testing." };
    authConfigs = {
      "auth-main": { type: "bearer_jwt", header: "Authorization", token },
    };
    defaultAuthRef = "auth-main";
  } else if (auth.includes("basic")) {
    const email = String(credentials.basicEmail || "").trim();
    const token = String(credentials.basicApiToken || "").trim();
    if (!email || !token) return { error: "Enter the Basic email and API token before testing." };
    authConfigs = {
      "auth-main": {
        type: "api_key",
        header: "Authorization",
        value: `Basic ${utf8ToBase64(`${email}:${token}`)}`,
      },
    };
    defaultAuthRef = "auth-main";
  } else if (auth.includes("api key")) {
    const header = String(credentials.apiKeyHeader || "").trim() || "X-API-Key";
    const value = String(credentials.apiKeyValue || "").trim();
    if (!value) return { error: "Enter the API key before testing." };
    authConfigs = {
      "auth-main": { type: "api_key", header, value },
    };
    defaultAuthRef = "auth-main";
  }

  return {
    app_config: {
      app_id: `gate2-probe-${Date.now()}`,
      app_name: "Gate 2 API connection",
      base_url: baseUrl,
      rate_limit_rpm: 60,
      ssl_verify: true,
      auth_configs: authConfigs,
      endpoints: [
        {
          id: "ep-1",
          method,
          path,
          operation: "Gate 2 connection test",
          purpose: "Gate 2 connection test",
          auth_ref: defaultAuthRef,
          request_schema: {},
          response_schema: {},
          test_data_source: { type: "synthetic" },
          risk_scope: [],
          ...(method === "POST" ? { request_body: {} } : {}),
        },
      ],
      collection: null,
    },
  };
}
